import { stripToDigits } from '@/lib/phone'

/**
 * @param {string} [phone]
 * @returns {string | null}
 */
export function normalizeWhatsAppPhone(phone) {
  const digits = stripToDigits(phone ?? '')
  return digits || null
}

/**
 * @param {string} isoDateTime
 */
export function formatAppointmentDateTime(isoDateTime) {
  const date = new Date(isoDateTime)
  const datePart = date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })

  return { datePart, timePart }
}

/**
 * @param {import('@/types/api').Appointment} appointment
 */
export function appointmentServiceNames(appointment) {
  const names = appointment.services
    ?.map((line) => line.service?.name)
    .filter(Boolean)

  if (names?.length) {
    return names.join(', ')
  }

  return 'Service'
}

/**
 * @param {object} params
 * @param {import('@/types/api').Appointment[]} params.appointments
 * @param {{ name?: string, phone?: string | null } | null} [params.customer]
 * @param {string} [params.salonName]
 * @param {string} [params.branchName]
 */
export function buildBookingWhatsAppMessage({
  appointments,
  customer,
  salonName = 'our salon',
  branchName,
}) {
  const primary = appointments[0]
  if (!primary) {
    return ''
  }

  const customerName = customer?.name ?? primary.customer?.name ?? 'there'
  const { datePart, timePart } = formatAppointmentDateTime(primary.starts_at)
  const services = appointmentServiceNames(primary)
  const stylistName = primary.stylist?.name

  const lines = [
    `Hello ${customerName},`,
    '',
    `Your appointment at ${salonName} is confirmed.`,
    '',
    `Date: ${datePart}`,
    `Time: ${timePart}`,
    `Service: ${services}`,
  ]

  if (stylistName) {
    lines.push(`Stylist: ${stylistName}`)
  }

  if (appointments.length > 1) {
    lines.push(
      '',
      `This booking includes ${appointments.length} recurring visits. The details above are for your first visit.`,
    )
  }

  if (primary.notes?.trim()) {
    lines.push('', `Notes: ${primary.notes.trim()}`)
  }

  lines.push('', 'We look forward to seeing you!')

  if (branchName && branchName !== salonName) {
    lines.push('', branchName)
  }

  return lines.join('\n')
}

/**
 * @param {{ phone?: string | null, message: string }} params
 */
export function buildWhatsAppSendUrl({ phone, message }) {
  const encoded = encodeURIComponent(message)
  const digits = phone ? normalizeWhatsAppPhone(phone) : null

  if (digits) {
    return `https://wa.me/${digits}?text=${encoded}`
  }

  return `https://wa.me/?text=${encoded}`
}

/**
 * Opens WhatsApp so staff can review and send the message manually.
 * @param {{ phone?: string | null, message: string }} params
 */
export function openWhatsAppBooking(params) {
  const url = buildWhatsAppSendUrl(params)
  window.open(url, '_blank', 'noopener,noreferrer')
}
